import { useState } from "react";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { ChevronDown, ChevronUp } from "lucide-react";
import Button from "../button/Button";

interface LazyExpandableContentProps {
  previewHeight: number;
  previewContent: string;
  loadFullDataQuery?: UseQueryOptions<string>;
  classNames?: string;
}

export function LazyExpandableContent({
  previewHeight = 120,
  previewContent,
  loadFullDataQuery: loadDataQuery,
  classNames,
}: LazyExpandableContentProps) {
  const [expanded, setExpanded] = useState(false);

  const { data, isFetching } = useQuery(
    loadDataQuery || {
      queryKey: ["lazy-expandable-content", previewContent],
      queryFn: async () => previewContent,
      enabled: expanded,
    }
  );

  return (
    <div className={`relative ${classNames}`}>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: expanded ? "none" : previewHeight }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(expanded ? data || "" : previewContent),
        }}
      ></div>

      {/* Bottom fade effect */}
      {!expanded && (
        <div className="pointer-events-none absolute bottom-8 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
      )}

      <Button
        size="sm"
        variant="ghost"
        onClick={() => setExpanded((v) => !v)}
        disabled={isFetching}
        className="mt-2 text-sm text-sky-600"
      >
        {isFetching ? (
          "Loading..."
        ) : expanded ? (
          <div className="flex items-center space-x-2">
            <div>Show less</div>
            <div>
              <ChevronUp size={13} />
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <div>Show more</div>
            <div>
              <ChevronDown size={13} />
            </div>
          </div>
        )}
      </Button>
    </div>
  );
}
